import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { istDateTime } from '@/utils/cricketCalendar';
import type { CricketFixture, CricketSeries } from '@/types/matchPushes';

const { Text } = Typography;
const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_FIXTURES = 100;

interface Props {
  open: boolean;
  eventId: string;
  onClose: () => void;
  /** Called after matches were added, so the schedule list reloads. */
  onAdded: () => void;
}

/**
 * Picks matches from the cricket calendar (a whole series, or ticked
 * matches) and adds them to the event as schedules. Matches already in this
 * event cannot be ticked.
 */
export const AddMatchesFromCalendarModal: React.FC<Props> = ({
  open,
  eventId,
  onClose,
  onAdded,
}) => {
  const [series, setSeries] = useState<CricketSeries[]>([]);
  const [seriesSlug, setSeriesSlug] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [trackedOnly, setTrackedOnly] = useState(false);
  const [fixtures, setFixtures] = useState<CricketFixture[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const inEvent = useCallback(
    (f: CricketFixture) => f.liveEvents?.some((e) => e.id === eventId) ?? false,
    [eventId]
  );

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    const now = Date.now();
    adminApi.cricketCalendar
      .listSeries({
        from: new Date(now - 7 * DAY_MS).toISOString(),
        to: new Date(now + 365 * DAY_MS).toISOString(),
      })
      .then((r) => setSeries(r.data.items))
      .catch((e) => message.error(apiErrorMessage(e, 'Failed to load the series')));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const now = Date.now();
    // With a series picked show all of it, otherwise the next 90 days.
    adminApi.cricketCalendar
      .listFixtures({
        from: new Date(now - (seriesSlug ? 365 : 1) * DAY_MS).toISOString(),
        to: new Date(now + (seriesSlug ? 365 : 90) * DAY_MS).toISOString(),
        series: seriesSlug,
        search: search.trim() || undefined,
        tracked: trackedOnly ? 'true' : undefined,
        limit: 200,
      })
      .then((r) => {
        if (cancelled) return;
        setFixtures(r.data.items.filter((f) => f.status !== 'cancelled'));
        setTruncated(r.data.total > r.data.items.length);
      })
      .catch((e) => {
        if (!cancelled) message.error(apiErrorMessage(e, 'Failed to load the matches'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, seriesSlug, search, trackedOnly]);

  const selectable = fixtures.filter((f) => !inEvent(f));
  const tooMany = selected.length > MAX_FIXTURES;
  const capped = selectable.length > MAX_FIXTURES;

  const add = async () => {
    setSaving(true);
    try {
      const response = await adminApi.addSchedulesFromFixtures(eventId, selected);
      const { created, skipped } = response.data;
      message.success(
        `${created} match${created === 1 ? '' : 'es'} added${skipped ? `, ${skipped} skipped (already in the event)` : ''}`
      );
      onAdded();
      onClose();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Failed to add the matches'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      width={860}
      title="Add matches from the calendar"
      okText={`Add ${selected.length || ''} match${selected.length === 1 ? '' : 'es'}`.replace(
        '  ',
        ' '
      )}
      okButtonProps={{ disabled: selected.length === 0 || tooMany, loading: saving }}
      onOk={add}
      onCancel={onClose}
      destroyOnClose
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Pick a series"
          style={{ width: 280 }}
          value={seriesSlug}
          onChange={(v) => {
            setSeriesSlug(v);
            setSelected([]);
          }}
          options={series.map((s) => ({
            value: s.seriesSlug,
            label: `${s.seriesName} (${s.fixtures})`,
          }))}
        />
        <Input.Search
          allowClear
          placeholder="Search teams or series"
          style={{ width: 220 }}
          onSearch={(value) => {
            setSearch(value);
            setSelected([]);
          }}
        />
        <Checkbox
          checked={trackedOnly}
          onChange={(e) => {
            setTrackedOnly(e.target.checked);
            setSelected([]);
          }}
        >
          Tracked only
        </Checkbox>
        {seriesSlug && (
          <Button
            size="small"
            onClick={() => setSelected(selectable.slice(0, MAX_FIXTURES).map((f) => f.id))}
          >
            {capped
              ? `Select the first ${MAX_FIXTURES} of ${selectable.length} in series`
              : `Select all ${selectable.length} in series`}
          </Button>
        )}
      </Space>
      {truncated && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="Only the first 200 matches are shown. Pick a series or search to narrow the list."
        />
      )}
      {capped && seriesSlug && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message={`At most ${MAX_FIXTURES} matches can be added at once, so "Select" takes the first ${MAX_FIXTURES}. Add the rest in a second round.`}
        />
      )}
      {tooMany && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 8 }}
          message={`At most ${MAX_FIXTURES} matches can be added at once.`}
        />
      )}
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={fixtures}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: (keys) => setSelected(keys as string[]),
          getCheckboxProps: (f) => ({ disabled: inEvent(f) }),
        }}
        columns={[
          {
            title: 'Starts',
            key: 'start',
            width: 140,
            render: (_: unknown, f: CricketFixture) => istDateTime(f.startsAt),
          },
          {
            title: 'Match',
            key: 'match',
            render: (_: unknown, f: CricketFixture) => (
              <div>
                <Text strong>
                  {f.team1Name} vs {f.team2Name}
                </Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {[f.matchLabel, f.seriesName].filter(Boolean).join(' · ')}
                  </Text>
                </div>
              </div>
            ),
          },
          {
            title: '',
            key: 'tags',
            render: (_: unknown, f: CricketFixture) => (
              <Space size={4} wrap>
                {inEvent(f) && <Tag color="green">Already in this event</Tag>}
                {!inEvent(f) && f.liveEvents?.length > 0 && (
                  <Tag>In {f.liveEvents.length} other event(s)</Tag>
                )}
                {f.status === 'finished' && <Tag>Finished</Tag>}
                {f.status === 'live' && <Tag color="red">Live</Tag>}
              </Space>
            ),
          },
        ]}
      />
    </Modal>
  );
};
